import { readJwt } from "../auth/storage";

/**
 * WebSocket client with JWT auth + exponential-backoff reconnect.
 *
 * - JWT is appended as `?token=<jwt>` query param (SPEC §13 + backend
 *   {@code JwtHandshakeInterceptor})
 * - Reconnects with 1s → 2s → 4s → 8s → 16s → 30s ceiling, reset on
 *   any successful open
 * - Distinguishes "closed by us" (no reconnect) from "dropped" (reconnect)
 * - Emits typed status updates so the dashboard can render the
 *   connection dot per SPEC §14 Status row
 *
 * Event payloads are forwarded raw; specific event-name handling
 * lives on top of this layer (per SPEC §14, names like
 * `position.opened`, `cycle.started`, `price.tick`).
 */

export type WsStatus =
  | "idle"
  | "connecting"
  | "open"
  | "closed"
  | "reconnecting"
  | "auth-failed";

export interface WsEvent {
  /** Event name per SPEC §14 (e.g. `price.tick`, `cycle.started`). */
  type: string;
  /** Server timestamp, ISO-8601. */
  ts?: string;
  /** Free-form payload — pages narrow per type. */
  data?: unknown;
}

export interface WsClientOptions {
  /** WS endpoint path, e.g. `/ws/ping` or `/ws/trading`. */
  path: string;
  onStatus?: (status: WsStatus) => void;
  onEvent?: (event: WsEvent) => void;
  /** Override JWT (testing). Defaults to {@link readJwt}. */
  getToken?: () => string | null;
  /** Override WebSocket ctor (testing). */
  socketFactory?: (url: string) => WebSocket;
  /** Override Math.random for jitter (testing). */
  rng?: () => number;
}

const BACKOFF_SCHEDULE_MS = [1_000, 2_000, 4_000, 8_000, 16_000, 30_000];
const MAX_JITTER_MS = 500;

export class WsClient {
  private readonly opts: WsClientOptions;
  private socket: WebSocket | null = null;
  private status: WsStatus = "idle";
  private retryIndex = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  /** Set when stop() is called — prevents reconnect after explicit close. */
  private stopped = false;

  constructor(opts: WsClientOptions) {
    this.opts = opts;
  }

  start(): void {
    this.stopped = false;
    this.connect();
  }

  stop(): void {
    this.stopped = true;
    this.clearReconnectTimer();
    if (this.socket) {
      this.socket.close(1000, "client-stop");
      this.socket = null;
    }
    this.setStatus("closed");
  }

  send(payload: string | object): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      throw new Error("ws not open");
    }
    const data = typeof payload === "string" ? payload : JSON.stringify(payload);
    this.socket.send(data);
  }

  getStatus(): WsStatus {
    return this.status;
  }

  // ---------- internals ----------

  private connect(): void {
    const token = (this.opts.getToken ?? readJwt)();
    if (!token) {
      this.setStatus("auth-failed");
      return;
    }
    this.setStatus(this.retryIndex === 0 ? "connecting" : "reconnecting");

    const url = buildUrl(this.opts.path, token);
    const factory =
      this.opts.socketFactory ?? ((u: string) => new WebSocket(u));
    let socket: WebSocket;
    try {
      socket = factory(url);
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.socket = socket;

    socket.onopen = () => {
      this.retryIndex = 0;
      this.setStatus("open");
    };

    socket.onmessage = (msg: MessageEvent) => {
      const event = parseEvent(msg.data);
      if (event) this.opts.onEvent?.(event);
    };

    socket.onerror = () => {
      // Browsers don't surface error details (security); we treat any
      // error as a soft signal and rely on the subsequent onclose to
      // trigger the reconnect path.
    };

    socket.onclose = (ev: CloseEvent) => {
      this.socket = null;
      if (this.stopped) {
        this.setStatus("closed");
        return;
      }
      // 4401 is reserved by JwtHandshakeInterceptor for auth failures;
      // 1008 (policy violation) is what Spring sends when the handshake
      // interceptor rejects. Either means our JWT is dead — stop retrying.
      if (ev.code === 4401 || ev.code === 1008) {
        this.setStatus("auth-failed");
        return;
      }
      this.scheduleReconnect();
    };
  }

  private scheduleReconnect(): void {
    this.setStatus("reconnecting");
    const base =
      BACKOFF_SCHEDULE_MS[Math.min(this.retryIndex, BACKOFF_SCHEDULE_MS.length - 1)];
    const rng = this.opts.rng ?? Math.random;
    const jitter = Math.floor(rng() * MAX_JITTER_MS);
    const delay = base + jitter;
    this.retryIndex++;
    this.clearReconnectTimer();
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (!this.stopped) this.connect();
    }, delay);
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer != null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private setStatus(next: WsStatus): void {
    if (this.status === next) return;
    this.status = next;
    this.opts.onStatus?.(next);
  }
}

export function buildUrl(path: string, token: string): string {
  // In the browser this resolves against window.location; in tests we
  // accept either a full URL or a path and reconstruct deterministically.
  const proto =
    typeof window !== "undefined" && window.location.protocol === "https:"
      ? "wss:"
      : "ws:";
  const host =
    typeof window !== "undefined" && window.location.host
      ? window.location.host
      : "localhost";
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `${proto}//${host}${cleanPath}?token=${encodeURIComponent(token)}`;
}

function parseEvent(raw: unknown): WsEvent | null {
  if (typeof raw !== "string") return null;
  try {
    const parsed = JSON.parse(raw) as WsEvent;
    if (parsed && typeof parsed.type === "string") return parsed;
    return null;
  } catch {
    return null;
  }
}
